import type {
    ChildSelector,
    IPropsChildren,
} from '../components/ui-element.types.js';
import type { UIElement } from '../components/index.js';
import { resolveChildDescriptor } from '../components/ui-element.helper.js';

/*
 * Defines a child element by selector or by IPropsChildren.
 */
export const Child = (
    params: IPropsChildren | ChildSelector,
    multiple = false
) => {
    return <T extends typeof UIElement>(
        _: void,
        context: ClassFieldDecoratorContext
    ) => {
        if (context.kind === 'field') {
            context.addInitializer(function (this: unknown) {
                const instance = this as InstanceType<T>;
                const propertyKey = String(context.name);
                let childDefinition: IPropsChildren;
                if (typeof params === 'string') {
                    childDefinition = { selector: params };
                } else if (params) {
                    childDefinition = {
                        ...params,
                        multiple,
                    };
                } else {
                    throw new Error(
                        '@Child and @Children decorator selector must have selector parameter'
                    );
                }

                const descriptor = resolveChildDescriptor(
                    propertyKey,
                    childDefinition.selector,
                    childDefinition.fallback,
                    multiple
                );
                Object.defineProperty(instance, propertyKey, descriptor);

                // This allows to keep backwards compatibility with legacy components,
                // which use `get static props() { ... }`
                const classConstructor = instance.constructor as T;
                classConstructor.props ||= {};
                classConstructor.props.children ||= {};
                classConstructor.props.children[propertyKey] = childDefinition;
            });
        } else {
            throw new Error('@Child decorator is only applied to the property');
        }
    };
};

/*
 * Defines the children of the element by selector or by ChildSelector.
 * Same as Child, but selects multiple children.
 */
export const Children = (params: IPropsChildren | ChildSelector) =>
    Child(params, true);
